import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { config } from "../../config";
import usericon from '../../assets/img/user_icon.png';
import handshakeicon from '../../assets/img/handshake_icon.png';
import commentsicon from '../../assets/img/comments_icon.png';
import shieldalticon from '../../assets/img/shield-alt_icon.png';
import { motion, useReducedMotion } from "framer-motion";
import {
    fadeUp,
    fadeUpReduced,
    scaleIn,
    scaleInReduced,
    stagger,
    slideInRightReduced,
    EASE,
    VIEWPORT_REVEAL,
} from "../../motion";

function Contact() {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reduced-motion-aware variants
    const reduce = useReducedMotion();
    const vFadeUp = reduce ? fadeUpReduced : fadeUp;
    const vScale = reduce ? scaleInReduced : scaleIn;
    const cardHover = reduce ? undefined : { y: -6, transition: { duration: 0.2, ease: EASE } };
    const approachSlide = reduce
        ? slideInRightReduced
        : { hidden: { opacity: 0, x: 40 }, show: { opacity: 1, x: 0, transition: { duration: 0.55, ease: EASE } } };

    // Scroll to top on component mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const validateForm = () => {
        const errors = {};

        // Full Name validation
        if (!formData.fullName.trim()) {
            errors.fullName = "Full name is required";
        } else if (formData.fullName.trim().length < 3) {
            errors.fullName = "Name must be at least 3 characters";
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            errors.email = "Email address is required";
        } else if (!emailRegex.test(formData.email)) {
            errors.email = "Please enter a valid email address";
        }

        // Phone validation: must start 07 and be exactly 11 digits
        const phoneRegex = /^07\d{9}$/;
        if (!formData.phone.trim()) {
            errors.phone = "Phone number is required";
        } else if (!phoneRegex.test(formData.phone)) {
            errors.phone = "Enter a valid UK mobile number (07xxxxxxxxx)";
        }

        // Subject is optional — no validation

        // Message validation
        if (!formData.message.trim()) {
            errors.message = "Message is required";
        } else if (formData.message.trim().length < 5) {
            errors.message = "Message must be at least 5 characters";
        }

        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsSubmitting(true);

        try {
            // Replace this URL with your actual API endpoint
            const response = await fetch(`${config.serverApi}/contact-us/submit`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                toast.success("Thank you! We've received your message and will get back to you soon.");
                const res = await response.json();
                console.log(279, res);

                // Reset form
                setFormData({
                    fullName: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                });
                setFormErrors({});
            } else {
                throw new Error("Failed to send message");
            }
        } catch (error) {
            setFormData({
                fullName: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            });
            setFormErrors({});
            console.error("Error submitting form:", error);
            toast.error("Oops! Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            {/* Banner Section */}
            <div className="contact-banner">
                <motion.div
                    className="banner-content"
                    variants={stagger}
                    initial="hidden"
                    whileInView="show"
                    viewport={VIEWPORT_REVEAL}
                >
                    <motion.h1 variants={vFadeUp}>We’re here to help</motion.h1>
                    <motion.p variants={vFadeUp}>If you have any questions, need support with your account, or would like to share feedback, please get in touch using the form below.</motion.p>
                    <motion.p variants={vFadeUp}> Our team reads every message carefully and aims to reply within 2–3 working days.</motion.p>
                </motion.div>
            </div>

            {/* Main Contact Section */}
            <div className="contact-main">
                <div className="container">
                    <div className="contact-wrapper">
                        {/* Contact Info Cards */}
                        <div className="contact-info-section">
                            <motion.div
                                className="info-header"
                                variants={vFadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={VIEWPORT_REVEAL}
                            >
                                <h2>You can reach us for</h2>
                                {/* <p>Multiple ways to reach us</p> */}
                            </motion.div>

                            <motion.div
                                className="info-cards"
                                variants={stagger}
                                initial="hidden"
                                whileInView="show"
                                viewport={VIEWPORT_REVEAL}
                            >
                                <motion.div className="info-card" variants={approachSlide} whileHover={cardHover}>
                                    <motion.div className="icon-wrapper" variants={vScale}>
                                        <img src={usericon} alt="" className="user_icon" />
                                    </motion.div>
                                    <h3>App or account support</h3>
                                </motion.div>

                                <motion.div className="info-card" variants={approachSlide} whileHover={cardHover}>
                                    <motion.div className="icon-wrapper" variants={vScale}>
                                        <img src={commentsicon} alt="" />
                                    </motion.div>
                                    <h3>Feedback or suggestions</h3>
                                </motion.div>

                                <motion.div className="info-card" variants={approachSlide} whileHover={cardHover}>
                                    <motion.div className="icon-wrapper" variants={vScale}>
                                        <img src={shieldalticon} alt="" />
                                    </motion.div>
                                    <h3>Safety or community concerns</h3>
                                </motion.div>

                                <motion.div className="info-card" variants={approachSlide} whileHover={cardHover}>
                                    <motion.div className="icon-wrapper handshake" variants={vScale}>
                                        <img src={handshakeicon} alt="" className="handshake_icon" />
                                    </motion.div>
                                    <h3>Partnership or press enquiries</h3>
                                </motion.div>
                            </motion.div>
                        </div>

                    </div>
                </div>
            </div>
            {/* Contact Form Section */}
            <div className="contact-form-section">
                <div className="container">
                    <motion.div
                        className="contact-form-wrapper"
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={VIEWPORT_REVEAL}
                    >
                        <div className="contact-header text-center">
                            {/* <h2>Get In Touch</h2> */}
                            <div className="contact-email">
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                                        stroke="#FFD6EC "
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M22 6L12 13L2 6"
                                        stroke="#FFD6EC"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <a href="mailto:info@lovehappiness.co.uk">
                                    info@lovehappiness.co.uk
                                </a>
                            </div>
                            <p className="contact-subtitle">
                                Or simply fill in the form below — we'll get back to you as soon
                                as we can.
                            </p>
                        </div>

                        <motion.form onSubmit={handleSubmit} className="contact-form" variants={vFadeUp}>
                            {/* Full Name Field */}
                            <div className="form-group">
                                <label htmlFor="fullName">Full Name</label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    placeholder="Enter your full name"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    className={formErrors.fullName ? "error" : ""}
                                />
                                {formErrors.fullName && (
                                    <span className="error-message">{formErrors.fullName}</span>
                                )}
                            </div>

                            {/* Email Address Field */}
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="We'll use this to reply to your message"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={formErrors.email ? "error" : ""}
                                />
                                {formErrors.email && (
                                    <span className="error-message">{formErrors.email}</span>
                                )}
                            </div>

                            {/* Phone Number Field */}
                            <div className="form-group">
                                <label htmlFor="phone">Phone Number</label>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    id="phone"
                                    name="phone"
                                    placeholder="Enter your phone number"
                                    value={formData.phone}
                                    onChange={(e) => {
                                        const clean = e.target.value.replace(/\D/g, "").slice(0, 11);
                                        setFormData((prev) => ({ ...prev, phone: clean }));
                                        if (formErrors.phone) {
                                            setFormErrors((prev) => ({ ...prev, phone: "" }));
                                        }
                                    }}
                                    maxLength={11}
                                    className={formErrors.phone ? "error" : ""}
                                />
                                {formErrors.phone && (
                                    <span className="error-message">{formErrors.phone}</span>
                                )}
                            </div>

                            {/* Subject Field */}
                            <div className="form-group">
                                <label htmlFor="subject">Subject <span className="optional-tag">(optional)</span></label>
                                <select
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    className={formErrors.subject ? "error" : ""}
                                >
                                    <option value="">Select the reason for your message</option>
                                    <option value="general">General Inquiry</option>
                                    <option value="support">Technical Support</option>
                                    <option value="feedback">Feedback</option>
                                    <option value="partnership">Partnership</option>
                                    <option value="press">Press & Media</option>
                                    <option value="other">Other</option>
                                </select>
                                {formErrors.subject && (
                                    <span className="error-message">{formErrors.subject}</span>
                                )}
                            </div>

                            {/* Message Field */}
                            <div className="form-group">
                                <label htmlFor="message">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="5"
                                    placeholder="Tell us a bit more about how we can help"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    className={formErrors.message ? "error" : ""}
                                ></textarea>
                                {formErrors.message && (
                                    <span className="error-message">{formErrors.message}</span>
                                )}
                            </div>

                            {/* Submit Button */}
                            <div className="form-submit">
                                <motion.button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={isSubmitting}
                                    whileHover={reduce ? undefined : { scale: 1.03 }}
                                    whileTap={reduce ? undefined : { scale: 0.97 }}
                                >
                                    {isSubmitting ? "Sending..." : "Send with Kindness"}
                                </motion.button>
                            </div>
                        </motion.form>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

export default Contact;
